import AsyncSelect from '@/components/Form/Shared/AsyncSelect'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import Paper from '@/components/Paper'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import CheckIcons from '@/icons/CheckIcons'
import CloseIcons from '@/icons/CloseIcons'
import { updateCustomerJourneyExplaination } from '@/modules/Customer/services'
import { CustomerJourney } from '@/modules/Customer/types'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { formatPGDate } from '@/utils/function'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { GridHookForm, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface JourneyInformationEditProps {
    selectedJourney: CustomerJourney
    customerId: number
    contactId: number
    onClose: () => void
    onSave: () => void
}

const JourneyInformationEdit = ({
    selectedJourney,
    customerId,
    onClose,
    onSave,
}: JourneyInformationEditProps) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'journeys',
        mode: 'contains',
    })
    const { customer_journey_explaination: explaination } = selectedJourney

    // Form control with default values
    const formControl = useForm({
        defaultValues: {
            service_interestings: explaination?.service_interestings || [],
            budget_explaination: explaination?.budget_explaination || '',
            sale_tracking_id: explaination?.sale_tracking_id || [],
            expected_date: explaination?.expected_date
                ? dayjs(explaination.expected_date)
                : null,
            influencer_explaination:
                explaination?.influencer_explaination || '',
            provider_explaination: explaination?.provider_explaination || '',
            problem_explaination: explaination?.problem_explaination || '',
            expected_explaination: explaination?.expected_explaination || '',
        },
    })

    // Update mutation
    const { mutateAsync: updateExplaination, isPending: isUpdating } =
        useMutationWithHandler(
            { mutationFn: updateCustomerJourneyExplaination },
            {
                reactQueryKeys: [
                    QUERY_KEYS.CUSTOMERS,
                    QUERY_KEYS.CUSTOMERS_JOURNEYS,
                ],
                successMessage: t('journey_updated_successfully'),
                errorMessage: t('failed_to_update_journey'),
                onSuccess: () => {
                    refetch()
                    onSave()
                    onClose()
                },
            }
        )

    const handleSubmit = async (data: any) => {
        try {
            const updatedExplaination = {
                ...explaination,
                service_interestings: data.service_interestings,
                budget_explaination:
                    parseFloat(data.budget_explaination?.toString()) || 0,
                expected_date: data?.expected_date
                    ? formatPGDate(data.expected_date, 'YYYY-MM-DDTHH:mm:ssZ')
                    : null,
                influencer_explaination: data.influencer_explaination,
                provider_explaination: data.provider_explaination,
                problem_explaination: data.problem_explaination,
                expected_explaination: data.expected_explaination,
            }

            await updateExplaination({
                customer_id: customerId,
                customer_journey_id: selectedJourney.customer_journey_id,
                params: updatedExplaination,
            })
        } catch (error) {
            console.error('Update failed:', error)
        }
    }

    return (
        <Paper className="h-full animate-slide-in-right p-4">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                <CheckIcons className="w-5 h-5 text-blue-600" />
                <Typogaphy className="text-lg font-semibold text-gray-800">
                    {t('edit_journey_information')}
                </Typogaphy>
            </div>

            <GridHookForm
                className="grid-cols-1 md:grid-cols-2 gap-4"
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        handler: onClose,
                        variant: 'outlined',
                        disabled: isUpdating,
                        startIcon: <CloseIcons />,
                    },
                    {
                        title: t('save_changes'),
                        type: 'submit',
                        variant: 'contained',
                        disabled: isUpdating,
                        loading: isUpdating,
                        startIcon: <CheckIcons />,
                    },
                ]}
                data={[
                    {
                        renderEditComponent: (control) => (
                            <AsyncSelect
                                asyncOptions={getProductsGroupOptions}
                                control={control}
                                name="service_interestings"
                                placeholder={t('customer_service_interestings')}
                                allowClear={false}
                                multiple
                                label={t('customer_service_interestings')}
                            />
                        ),
                        className: 'md:col-span-2 col-span-4',
                    },
                    {
                        renderEditComponent: (control) => (
                            <EmployeeSelect
                                control={control}
                                name="sale_tracking_id"
                                multiple
                            />
                        ),
                        className: 'md:col-span-2 col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'influencer_explaination',
                            type: 'textfield',
                            label: t('influencer_explaination'),
                            placeholder: t('enter_decision_maker'),
                        },
                        className: 'md:col-span-2 col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'budget_explaination',
                            type: 'number',
                            label: t('budget_explaination'),
                            placeholder: t('enter_budget_amount'),
                        },
                    },
                    {
                        formItemProps: {
                            name: 'expected_date',
                            type: 'datepicker',
                            label: t('explainations.expected_date'),
                            placeholder: t('enter_expected_date'),
                        },
                    },
                    {
                        formItemProps: {
                            name: 'problem_explaination',
                            type: 'textarea',
                            label: t('problem_explaination'),
                            placeholder: t(
                                'describe_customer_problems_and_challenges'
                            ),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'expected_explaination',
                            type: 'textarea',
                            label: t('expected_explaination'),
                            placeholder: t(
                                'describe_customer_expectations_and_goals'
                            ),
                        },
                        className: 'col-span-4',
                    },
                ]}
                formControl={formControl}
                onSubmit={handleSubmit}
            />
        </Paper>
    )
}
export default JourneyInformationEdit
